import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'

type Schema = components['schemas']

export type SkillReference = Schema['SkillReferenceDto']
export type AbilityReference = Schema['AbilityReferenceDto']

/**
 * Reference data is master data: it changes when someone edits the seed, not
 * during a session. So it is fetched once and never refetched — `staleTime:
 * Infinity` is the point, not a tuning knob.
 *
 * It exists at all so the client hard-codes no domain values: the skill list, the
 * enum catalogs and the ability templates all come from the server.
 */
const FOREVER = { staleTime: Number.POSITIVE_INFINITY, gcTime: Number.POSITIVE_INFINITY } as const

export const referenceKeys = {
  all: ['reference'] as const,
  skills: () => [...referenceKeys.all, 'skills'] as const,
  enums: () => [...referenceKeys.all, 'enums'] as const,
  abilities: () => [...referenceKeys.all, 'abilities'] as const,
}

export function useSkillReference(): UseQueryResult<SkillReference[]> {
  return useQuery({
    queryKey: referenceKeys.skills(),
    queryFn: () => api.get<SkillReference[]>('/reference/skills'),
    ...FOREVER,
  })
}

export function useEnumCatalog(): UseQueryResult<Schema['EnumCatalogDto']> {
  return useQuery({
    queryKey: referenceKeys.enums(),
    queryFn: () => api.get<Schema['EnumCatalogDto']>('/reference/enums'),
    ...FOREVER,
  })
}

/**
 * The seeded ability templates.
 *
 * Fetched whole and filtered in the browser rather than re-queried per keystroke:
 * the endpoint supports `search` and `type`, but the list is a few dozen rows of
 * data that never changes within a session, so a round trip per character typed
 * would be worse in every way. The server-side filter earns its keep for clients
 * that cannot cache — and it is what makes the endpoint useful beyond this app.
 */
export function useAbilityReference(): UseQueryResult<AbilityReference[]> {
  return useQuery({
    queryKey: referenceKeys.abilities(),
    queryFn: () => api.get<AbilityReference[]>('/reference/abilities'),
    ...FOREVER,
  })
}

/** Narrows the cached templates the same way the server would. */
export function filterAbilities(
  abilities: readonly AbilityReference[],
  search: string,
  type?: Schema['AbilityType'],
): AbilityReference[] {
  const needle = search.trim().toLowerCase()

  return abilities.filter((ability) => {
    if (type !== undefined && ability.type !== type) return false
    if (needle === '') return true
    return (
      (ability.name ?? '').toLowerCase().includes(needle) ||
      (ability.description ?? '').toLowerCase().includes(needle)
    )
  })
}
